import { useEffect, useMemo, useState } from "react"
import apiClient from "../../lib/api"
import type { IMediaFile } from "@dc-ireland/shared"
import { isImageUrl, resolveImageUrl } from "../../lib/imageUtils"

type ImagePickerProps = {
  label: string
  value: string
  onChange: (url: string) => void
  hint?: string
  required?: boolean
}

const ImagePicker = ({ label, value, onChange, hint, required }: ImagePickerProps) => {
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [uploadName, setUploadName] = useState("")
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [media, setMedia] = useState<IMediaFile[]>([])
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [query, setQuery] = useState("")

  const loadMedia = async () => {
    setLoadingMedia(true)
    setError(null)
    try {
      const files = await apiClient.getMediaFiles()
      setMedia(files.filter((file) => isImageUrl(file.url, file.mimeType)))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load media library")
    } finally {
      setLoadingMedia(false)
    }
  }

  useEffect(() => {
    if (libraryOpen) void loadMedia()
  }, [libraryOpen])

  const handleUpload = async (file: File) => {
    setUploading(true)
    setError(null)
    try {
      const result = await apiClient.uploadFile(file, uploadName.trim() || undefined)
      onChange(result.url)
      setUploadName("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setUploading(false)
    }
  }

  const filteredMedia = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return media
    return media.filter((file) =>
      [file.name, file.filename].some((field) => field?.toLowerCase().includes(needle))
    )
  }, [media, query])

  const previewUrl = resolveImageUrl(value)

  return (
    <div className="form-control">
      <span className="label-text">{label}{required ? " *" : ""}</span>
      {hint && <span className="label-text-alt text-xs">{hint}</span>}

      {error && <p className="mt-1 text-sm text-error">{error}</p>}

      <div className="mt-2 flex flex-wrap gap-4">
        <div className="relative h-28 w-44 overflow-hidden rounded-lg border border-base-300 bg-base-200">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center px-2 text-center text-sm text-secondary">
              No image selected
            </div>
          )}
        </div>

        <div className="flex min-w-[220px] flex-1 flex-col gap-2">
          <input
            className="input input-bordered input-sm"
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            placeholder="File name (optional)"
          />
          <label className="btn btn-outline btn-sm w-fit">
            {uploading ? "Uploading…" : "Upload image"}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleUpload(file)
                e.target.value = ""
              }}
            />
          </label>
          <button
            type="button"
            className="btn btn-ghost btn-sm w-fit"
            onClick={() => setLibraryOpen(true)}
          >
            Choose from library
          </button>
          {value && (
            <button type="button" className="btn btn-ghost btn-sm w-fit text-error" onClick={() => onChange("")}>
              Clear
            </button>
          )}
        </div>
      </div>

      {libraryOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-4xl">
            <h3 className="text-lg font-bold">Media library</h3>
            <p className="py-2 text-sm text-secondary">Select an uploaded image or upload new files from the File Upload page.</p>
            <input
              type="search"
              className="input input-bordered input-sm mb-3 w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name"
              aria-label="Search media library by name"
            />

            {loadingMedia ? (
              <span className="loading loading-spinner loading-md" />
            ) : media.length === 0 ? (
              <p className="py-6 text-secondary">No images uploaded yet.</p>
            ) : filteredMedia.length === 0 ? (
              <p className="py-6 text-secondary">No images match “{query.trim()}”.</p>
            ) : (
              <div className="grid max-h-[50vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4 md:grid-cols-5">
                {filteredMedia.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    className={`overflow-hidden rounded-lg border text-left transition hover:border-primary ${
                      value === file.url ? "border-primary ring-2 ring-primary/30" : "border-base-300"
                    }`}
                    onClick={() => {
                      onChange(file.url)
                      setLibraryOpen(false)
                    }}
                  >
                    <img
                      src={resolveImageUrl(file.url)}
                      alt={file.name}
                      className="h-20 w-full object-cover"
                    />
                    <div className="truncate px-2 py-1 text-xs font-medium">{file.name}</div>
                  </button>
                ))}
              </div>
            )}

            <div className="modal-action">
              <button type="button" className="btn" onClick={() => setLibraryOpen(false)}>
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setLibraryOpen(false)}>
              close
            </button>
          </form>
        </dialog>
      )}
    </div>
  )
}

export default ImagePicker
